import * as z from 'zod';

import { AttachmentOwnerTypeSchema } from '../common/enums.js';
import { PageQuerySchema, paginated } from '../common/pagination.js';
import {
  DescriptionSchema,
  ExternalUrlSchema,
  IdentifierSchema,
  IsoDateTimeSchema,
  LabelSchema,
  ShortTextSchema,
} from '../common/primitives.js';

/**
 * attachments context — uploaded screenshots/documents and external links.
 *
 * Uploads are presigned: the browser PUTs straight to S3/MinIO and then
 * confirms, so no file bytes ever pass through the API. External links are
 * stored as data and never fetched by the server (architecture §6, closes SSRF
 * by design).
 */

export const ATTACHMENT_CONTENT_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/svg+xml',
  'application/pdf',
  'text/markdown',
  'text/plain',
  'application/json',
  'application/zip',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
] as const;
export const AttachmentContentTypeSchema = z.enum(ATTACHMENT_CONTENT_TYPES);
export type AttachmentContentType = z.infer<typeof AttachmentContentTypeSchema>;

export const ATTACHMENT_STATES = ['PENDING', 'STORED', 'FAILED'] as const;
export const AttachmentStateSchema = z.enum(ATTACHMENT_STATES);
export type AttachmentState = z.infer<typeof AttachmentStateSchema>;

/**
 * Upload names are display-only; the object key is server-minted.
 *
 * The C0 range below is spelled as a literal escape range rather than `\p{Cc}`
 * on purpose: this `source` is published verbatim as an OpenAPI `pattern`, and
 * JSON Schema carries no regex flags, so a consumer compiling it without `u`
 * would read `\p{Cc}` as the character set `pCc{}` and silently invert the
 * check. That is why `no-control-regex` is switched off for this file.
 */
export const FileNameSchema = z
  .string()
  .min(1)
  .max(255)
  .regex(
    /^[^/\\<>:"|?*\u0000-\u001f]+$/u,
    'file name must not contain path or control characters',
  );

export const AttachmentSchema = z.strictObject({
  id: IdentifierSchema,
  ownerType: AttachmentOwnerTypeSchema,
  ownerId: z.string().min(1).max(200),
  fileName: FileNameSchema,
  contentType: AttachmentContentTypeSchema,
  sizeBytes: z.int().min(0),
  state: AttachmentStateSchema,
  /** Short-lived download URL, reissued on every read. Never a public bucket URL. */
  downloadUrl: ExternalUrlSchema.nullable(),
  caption: DescriptionSchema.nullable(),
  checksumSha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .nullable(),
  uploadedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  createdBy: ShortTextSchema,
});
export type Attachment = z.infer<typeof AttachmentSchema>;

export const ExternalLinkSchema = z.strictObject({
  id: IdentifierSchema,
  ownerType: AttachmentOwnerTypeSchema,
  ownerId: z.string().min(1).max(200),
  label: LabelSchema,
  url: ExternalUrlSchema,
  note: DescriptionSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  createdBy: ShortTextSchema,
});
export type ExternalLink = z.infer<typeof ExternalLinkSchema>;

export const PresignUploadRequestSchema = z.strictObject({
  ownerType: AttachmentOwnerTypeSchema,
  ownerId: z.string().min(1).max(200),
  fileName: FileNameSchema,
  contentType: AttachmentContentTypeSchema,
  /** Declared up front so the server can refuse oversize uploads before signing. */
  sizeBytes: z.int().min(1).max(1_073_741_824),
  caption: DescriptionSchema.optional(),
});
export type PresignUploadRequest = z.infer<typeof PresignUploadRequestSchema>;
export const PresignUploadResponseSchema = z.strictObject({
  attachment: AttachmentSchema,
  uploadUrl: ExternalUrlSchema,
  uploadMethod: z.enum(['PUT', 'POST']),
  /** Headers the browser must echo for the signature to verify. */
  requiredHeaders: z.record(z.string(), z.string()),
  expiresAt: IsoDateTimeSchema,
});
export type PresignUploadResponse = z.infer<typeof PresignUploadResponseSchema>;

export const ConfirmUploadRequestSchema = z.strictObject({
  attachmentId: IdentifierSchema,
  sizeBytes: z.int().min(1).max(1_073_741_824),
  checksumSha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .optional(),
});
export type ConfirmUploadRequest = z.infer<typeof ConfirmUploadRequestSchema>;
export const ConfirmUploadResponseSchema = z.strictObject({ attachment: AttachmentSchema });
export type ConfirmUploadResponse = z.infer<typeof ConfirmUploadResponseSchema>;

export const ListAttachmentsRequestSchema = z.strictObject({
  ...PageQuerySchema.shape,
  ownerType: AttachmentOwnerTypeSchema.optional(),
  ownerId: z.string().min(1).max(200).optional(),
  state: AttachmentStateSchema.optional(),
});
export type ListAttachmentsRequest = z.infer<typeof ListAttachmentsRequestSchema>;
export const ListAttachmentsResponseSchema = paginated(AttachmentSchema);
export type ListAttachmentsResponse = z.infer<typeof ListAttachmentsResponseSchema>;

export const DeleteAttachmentRequestSchema = z.strictObject({
  attachmentId: IdentifierSchema,
});
export type DeleteAttachmentRequest = z.infer<typeof DeleteAttachmentRequestSchema>;
export const DeleteAttachmentResponseSchema = z.strictObject({
  attachmentId: IdentifierSchema,
  deletedAt: IsoDateTimeSchema,
});
export type DeleteAttachmentResponse = z.infer<typeof DeleteAttachmentResponseSchema>;

export const AddExternalLinkRequestSchema = z.strictObject({
  ownerType: AttachmentOwnerTypeSchema,
  ownerId: z.string().min(1).max(200),
  label: LabelSchema,
  url: ExternalUrlSchema,
  note: DescriptionSchema.optional(),
});
export type AddExternalLinkRequest = z.infer<typeof AddExternalLinkRequestSchema>;
export const AddExternalLinkResponseSchema = z.strictObject({ link: ExternalLinkSchema });
export type AddExternalLinkResponse = z.infer<typeof AddExternalLinkResponseSchema>;

export const RemoveExternalLinkRequestSchema = z.strictObject({
  linkId: IdentifierSchema,
});
export type RemoveExternalLinkRequest = z.infer<typeof RemoveExternalLinkRequestSchema>;
export const RemoveExternalLinkResponseSchema = z.strictObject({
  linkId: IdentifierSchema,
  deletedAt: IsoDateTimeSchema,
});
export type RemoveExternalLinkResponse = z.infer<typeof RemoveExternalLinkResponseSchema>;
